/**
 * **A cor dos efeitos de carta, escolhida pelo jogador** (gate V28H.O).
 *
 * ## Porque isto existe
 *
 * Pedido do dono, no mesmo momento em que ele corrigiu o Turbilhão: "adicione a
 * opção do usuário poder escolher a cor do efeito também". O círculo mágico e a
 * serra são desenhados em BRANCO e recebem uma tinta - portanto a escolha é
 * livre, e não uma lista de cores que alguém teve de prever no dia em que
 * gerou as texturas.
 *
 * ## Porque é do BROWSER e não do servidor
 *
 * Porque não é economia. Nada aqui concede, gasta ou prova o que quer que seja:
 * é o aspecto de um efeito no ecrã de quem joga. Mandá-la para o servidor
 * gastaria uma rota e uma coluna para guardar uma preferência que só interessa
 * a este monitor - e a autoridade existe para o que pode ser roubado.
 *
 * A forma é a mesma do volume (`audio/master.ts`), de propósito: uma chave
 * versionada, leituras que nunca lançam, e escritas que falham em silêncio. Um
 * `localStorage` inacessível - janela privada, dados bloqueados, uma captura de
 * miniatura - é um caso ordinário, e a resposta certa é o padrão e não um ecrã
 * partido.
 */

/**
 * A cor por omissão: o ciano néon com que estes efeitos nasceram.
 *
 * **PROVISÓRIA**, como todo o equilíbrio visual deste gate. Quem nunca abrir a
 * paleta vê exactamente o que via antes de ela existir.
 */
export const CARD_COLOUR_DEFAULT = 0x9b_e7_ff;

/**
 * A chave onde ela fica.
 *
 * Versionada: se um dia a forma guardada mudar, uma chave nova é a diferença
 * entre ignorar o que estava lá e interpretá-lo mal.
 */
export const CARD_COLOUR_STORAGE_KEY = 'kenocyber:card-colour:v1';

/**
 * As cores oferecidas de imediato.
 *
 * Uma paleta E um selector livre: a paleta é o caminho de um clique para quem
 * só quer trocar, e o selector nativo é o que impede a paleta de ser um tecto.
 * O vermelho está aqui porque é a cor da referência que o dono desenhou.
 */
export const CARD_COLOUR_PRESETS: readonly { readonly name: string; readonly value: number }[] =
  Object.freeze([
    { name: 'Ciano', value: 0x9b_e7_ff },
    { name: 'Vermelho', value: 0xd1_18_28 },
    { name: 'Violeta', value: 0xb2_8c_ff },
    { name: 'Verde', value: 0x2f_d0_8a },
    { name: 'Âmbar', value: 0xff_c4_6e },
    { name: 'Magenta', value: 0xff_5c_c8 },
    { name: 'Azul', value: 0x4d_8b_ff },
    { name: 'Branco', value: 0xff_ff_ff },
  ]);

/** O mínimo de `localStorage` que este módulo usa. */
export interface CardColourStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Um inteiro de cor utilizável, venha de onde vier.
 *
 * Fora de `0x000000..0xffffff` volta ao padrão, e não é aparado: uma cor
 * impossível é um valor corrompido, e adivinhar o que ele queria dizer seria
 * pintar o ecrã com o resultado de um erro.
 */
export function clampCardColour(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number.NaN;
  if (!Number.isInteger(numeric) || numeric < 0 || numeric > 0xff_ff_ff) {
    return CARD_COLOUR_DEFAULT;
  }
  return numeric;
}

/** `0x9be7ff` para `#9be7ff`, que é o que um `input[type=color]` fala. */
export function cardColourToHex(value: number): string {
  return `#${clampCardColour(value).toString(16).padStart(6, '0')}`;
}

/** E de volta. Um texto que não seja uma cor devolve o padrão. */
export function cardColourFromHex(text: string): number {
  if (!/^#[0-9a-fA-F]{6}$/.test(text)) {
    return CARD_COLOUR_DEFAULT;
  }
  return Number.parseInt(text.slice(1), 16);
}

/** Lê o que estava guardado, ou o padrão. Nunca lança. */
export function readCardColour(storage: CardColourStorage | undefined | null): number {
  if (!storage) {
    return CARD_COLOUR_DEFAULT;
  }
  try {
    const raw = storage.getItem(CARD_COLOUR_STORAGE_KEY);
    if (raw === null) {
      return CARD_COLOUR_DEFAULT;
    }
    return clampCardColour(Number.parseInt(raw, 10));
  } catch {
    return CARD_COLOUR_DEFAULT;
  }
}

/** E escreve, com a mesma tolerância. */
function writeCardColour(storage: CardColourStorage | undefined | null, value: number): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(CARD_COLOUR_STORAGE_KEY, String(value));
  } catch {
    /*
     * Guardar falhou, e a cor continua a valer NESTA sessão.
     *
     * Uma quota cheia ou um armazenamento bloqueado não são razão para a paleta
     * deixar de responder: perde-se a memória entre recargas, não o controlo.
     */
  }
}

/**
 * **A cor viva, com quem a observa.**
 *
 * Uma caixa e não um `useState` no topo da aplicação, e a razão é a cena: o
 * Phaser desenha a serra sessenta vezes por segundo e não pode subscrever React.
 * Ele lê `current()` no quadro; o painel escreve `set()`; e quem quiser reagir
 * subscreve. Ninguém tem de re-renderizar uma árvore para uma serra mudar de
 * cor.
 */
export class CardColourStore {
  private value: number;
  private readonly storage: CardColourStorage | undefined | null;
  private readonly listeners = new Set<(value: number) => void>();

  constructor(storage: CardColourStorage | undefined | null) {
    this.storage = storage;
    this.value = readCardColour(storage);
  }

  current(): number {
    return this.value;
  }

  set(value: number): void {
    const next = clampCardColour(value);
    if (next === this.value) {
      return;
    }
    this.value = next;
    writeCardColour(this.storage, next);
    for (const listener of this.listeners) {
      listener(next);
    }
  }

  /** Devolve a função que cancela a subscrição, como o resto da aplicação faz. */
  subscribe(listener: (value: number) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

/**
 * A instância que a aplicação usa.
 *
 * `globalThis.localStorage` lido com cuidado: em Node - as provas, uma captura
 * de miniatura - ele não existe, e o acesso a algumas implementações LANÇA em
 * vez de devolver `undefined`.
 */
function ambientStorage(): CardColourStorage | null {
  try {
    return (globalThis as { localStorage?: CardColourStorage }).localStorage ?? null;
  } catch {
    return null;
  }
}

export const cardColourStore = new CardColourStore(ambientStorage());

/**
 * **A cor de TODOS os efeitos de carta é a que o jogador escolheu** (V28H.R).
 *
 * ## A regra anterior, e porque ela caiu
 *
 * Houve uma versão em que a cor dizia de QUEM era o efeito: a sua na escolha
 * dele, a de um aliado em ciano, a de um inimigo em vermelho. O dono cancelou-a
 * antes de a ver montada, e a razão é boa: uma paleta que só vale para a própria
 * nave não é uma paleta - é uma decoração que desaparece assim que há mais
 * alguém no ecrã.
 *
 * ## Quem diz de quem é, então
 *
 * A **barra de vida** - verde do meu lado, vermelha do outro - e o **nickname**
 * por cima do casco. Duas coisas que são sobre a NAVE, e não sobre o efeito: é a
 * nave que pertence a alguém, e é ela que tem de ser identificada.
 *
 * Uma função e não `cardColourStore.current()` espalhado: quem desenhar uma
 * carta nova pergunta aqui, e o dia em que a regra mudar outra vez tem um sítio
 * só para mudar.
 */
export function cardEffectTint(): number {
  return cardColourStore.current();
}
